import LightCustomButton from "@components/buttons/LightCustomButton";
import LabelRow from "@components/rowcomponents/LabelRow";
import { cashierApis } from "@services/apis/Cashier";
import {
  GetCashierInfoData,
  GetCashierInfoResponse,
} from "@services/models/Cashier/GetCashierInfoModel";
import { SetCashierStatusRequest } from "@services/models/Cashier/SetCashierStatusModel";
import React, { useEffect, useState } from "react";
import ConstantFunctions from "../../utils/constants_functions";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import CashierDetailsSkeleton from "./components/CashierDetailsSkeleton";

interface CashierDetailsProps {
  userId: number;
}

const CashierDetails: React.FC<CashierDetailsProps> = ({ userId }) => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const [data, setData] = useState<GetCashierInfoData>();
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const constantFunctions = new ConstantFunctions();
  const [upiidsOpen, setUpiidsOpen] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response: GetCashierInfoResponse = await cashierApis.getCashierInfo(
        { userId: userId.toString() }
      );
      setData(response.data[0]);
    } catch (error: any) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const handleCashierStatus = async (state: string) => {
    try {
      setIsButtonLoading(true);
      const toggleCashierStatusRequest: SetCashierStatusRequest = {
        userId: userId.toString(),
        setStatus: state,
      };

      const data = await cashierApis.setCashierStatus(
        toggleCashierStatusRequest
      );
      handleSuccess({ message: data.msg || "Status changed successfully" });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Updating status failed. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setIsButtonLoading(false);
    }
  };

  if (error) {
    return <div>Fetch error</div>;
  }

  if (isLoading) {
    return <CashierDetailsSkeleton />;
  }

  return (
    <div className="flex flex-col gap-[12px] mt-[20px] mr-[16px]">
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row justify-between">
          <div className="bg-bg-page-body py-[4px] px-[12px] text-text-base font-bold text-[10px] rounded">
            UID: {data?.userId}
          </div>
          <div className="bg-bg-page-body py-[4px] px-[12px] text-text-base font-bold text-[10px] rounded">
            Status:{" "}
            {data?.cashierState === 0
              ? "DELETED"
              : data?.cashierState === 1
              ? "ACTIVE"
              : "SUSPENDED"}
          </div>
        </div>

        <LabelRow label="Username" value={data?.username ?? ""} />

        <LabelRow label="Tylt Email" value={data?.emailId ?? ""} />

        <LabelRow label="Contact Number" value={data?.whatsappNumber ?? ""} />

        <LabelRow label="Area Manager" value={data?.area_manager ?? ""} />

        {data?.upi_handles && data?.upi_handles.length > 0 && (
          <div className="flex flex-col items-start space-y-[8px]">
            <div className="flex flex-row justify-between items-center w-full">
              <span className="text-text-base text-[12px] font-normal">
                UPI IDs
              </span>
              <div className="flex flex-row items-center gap-[4px]">
                <span className="text-text-base text-[12px] font-normal">
                  #{data?.upi_handles.length}
                </span>
                <div
                  className="h-[16px] w-[16px] rounded-[2px] bg-bg-button-secondary flex items-center justify-center cursor-pointer"
                  onClick={() => setUpiidsOpen(!upiidsOpen)}
                >
                  <span className="material-symbols-outlined text-[12px] text-text-base">
                    {upiidsOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                  </span>
                </div>
              </div>
            </div>

            {upiidsOpen && (
              <ul className="list-disc ml-4 text-[12px] text-text-base space-y-[4px]">
                {data.upi_handles.map((upiId: string, index: number) => (
                  <li key={index}>{upiId}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <LabelRow
          label="Current USDT Balance"
          value={constantFunctions.formatCurrency(Number(data?.wallet))}
        />

        <LabelRow
          label="Total Trade Value Completed (day)"
          value={
            data?.TV_USD_SuccessToday
              ? constantFunctions.formatCurrency(
                  Number(data?.TV_USD_SuccessToday)
                )
              : "0"
          }
        />

        <LabelRow
          label="Total USDT in Escrow"
          value={constantFunctions.formatCurrency(Number(data?.escrow))}
        />

        <LabelRow
          label="Number of Order Fulfilled Daily"
          value={`${data?.successCountToday ?? 0}`}
        />
      </div>
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row items-center justify-between">
          {data?.cashierState !== 0 && (
            <LightCustomButton
              text="DELETE CASHIER"
              isButtonLoading={isButtonLoading}
              isActive={true}
              textSize="10px"
              onClick={() => {
                handleCashierStatus("delete");
              }}
            />
          )}
          {data?.cashierState !== 2 && (
            <LightCustomButton
              text="SUSPEND CASHIER"
              isButtonLoading={isButtonLoading}
              isActive={true}
              textSize="10px"
              buttonColor="bg-bg-status-red"
              borderColor="border-bg-status-red"
              onClick={() => {
                handleCashierStatus("suspend");
              }}
            />
          )}
          {data?.cashierState !== 1 && (
            <LightCustomButton
              text="ACTIVATE CASHIER"
              isButtonLoading={isButtonLoading}
              isActive={true}
              textSize="10px"
              onClick={() => {
                handleCashierStatus("active");
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CashierDetails;
